import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Send, Loader2, MessageCircle, BookOpen, ShieldAlert, Heart, Phone } from "lucide-react";
import { waLink } from "@/lib/constants";

const QUICK_PROMPTS = [
  "Copilul nu mai vrea să mă vadă - ce să fac?",
  "Cum recunosc semnele alienării parentale?",
  "Ce documente îmi sunt utile în instanță?",
  "Am nevoie de ajutor psihologic pentru copil.",
];

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        data-testid={`chat-msg-${role}`}
        className={`max-w-[85%] md:max-w-[75%] rounded-lg px-4 py-3 text-[14px] leading-relaxed whitespace-pre-line ${
          isUser
            ? "bg-[#D4A63D] text-[#0A1020] rounded-br-sm"
            : "bg-[#131B2E] border border-white/10 text-[#F8F8F4] rounded-bl-sm"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

export default function ChatHelper() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem("ts_chat_session") || "";
    } catch {
      return "";
    }
  });
  const endRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/chat/history/${sessionId}`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    setInput("");
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId || undefined, message });
      if (data?.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem("ts_chat_session", data.session_id); } catch {}
      }
      setMessages((m) => [...m, { role: "assistant", content: data?.reply || "(răspuns gol)" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Asistentul AI nu este disponibil momentan.";
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId("");
    try { localStorage.removeItem("ts_chat_session"); } catch {}
  };

  return (
    <div data-testid="page-chat-helper" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <header className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8] mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Asistent AI · Beta
        </div>
        <h1 className="font-display text-3xl md:text-5xl text-[#F8F8F4]">Asistent de ghidare</h1>
        <p className="mt-3 text-[#A0AABF] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          Discuții confidențiale orientate spre informare. <span className="text-[#F8F8F4]">Nu înlocuiește </span>
          consultanța juridică sau psihologică.
        </p>
      </header>

      {/* SAFETY DISCLAIMER */}
      <div className="cinematic-card rounded-lg p-4 mb-6 border-[#E89BB8]/20 bg-[#E89BB8]/5 flex gap-3 items-start" data-testid="chat-safety-banner">
        <ShieldAlert className="w-5 h-5 text-[#E89BB8] shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-[#F8F8F4]/85 leading-relaxed">
          <strong className="text-[#F8F8F4]">În situații de criză sau pericol imediat:</strong>
          <span className="block mt-1 text-[#A0AABF]">
            Sună <a href="tel:112" className="text-[#D4A63D] font-semibold">112</a> (urgență),
            <a href="tel:0800801200" className="text-[#D4A63D] font-semibold"> 0800 801 200</a> (Telefonul Copilului)
            sau accesează <a href="/ajutor" className="text-[#D4A63D] underline">/ajutor</a>.
          </span>
        </div>
      </div>

      {/* CHAT WINDOW */}
      <div className="cinematic-card rounded-lg overflow-hidden flex flex-col" style={{ minHeight: "55vh" }}>
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-[#0A1020]/60">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#3BA76D] animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-[#A0AABF]">Asistent online</span>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={reset}
              data-testid="chat-reset"
              className="text-[10px] uppercase tracking-widest text-[#6C7A9C] hover:text-[#E89BB8]"
            >
              Conversație nouă
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <MessageCircle className="w-10 h-10 text-[#D4A63D]/60 mx-auto mb-3" />
              <p className="text-[14px] text-[#A0AABF] max-w-md mx-auto">
                Salut. Sunt aici să te ascult și să te orientez. Spune-mi în câteva cuvinte ce te aduce azi pe TatiSiSofia.ro.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => send(p)}
                    data-testid={`chat-prompt-${i}`}
                    className="text-[11.5px] px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4A63D]/40 text-[#F8F8F4]/85 hover:text-[#D4A63D] rounded-sm transition-colors text-left"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} />
          ))}
          {loading && (
            <div className="flex justify-start mb-3">
              <div className="bg-[#131B2E] border border-white/10 text-[#A0AABF] rounded-lg rounded-bl-sm px-4 py-3 inline-flex items-center gap-2 text-[13px]">
                <Loader2 className="w-4 h-4 animate-spin text-[#D4A63D]" /> Asistentul scrie...
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-3 md:p-4 border-t border-white/5 bg-[#0A1020]/60 flex gap-2"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Scrie un mesaj... (max 3000 caractere)"
            maxLength={3000}
            disabled={loading}
            className="flex-1 bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-sm text-[#F8F8F4] placeholder-white/35 focus:border-[#D4A63D] outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={loading || !input.trim()}
            className="btn-gold rounded-sm px-5 py-3 text-[11px] font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> TRIMITE
          </button>
        </form>
      </div>

      {/* QUICK ACTIONS BELOW CHAT */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <a href="/programare" className="dense-card p-4 flex items-center gap-3 group" data-testid="chat-cta-booking">
          <Heart className="w-5 h-5 text-[#D4A63D]" />
          <div>
            <div className="text-[12.5px] text-[#F8F8F4] font-medium group-hover:text-[#D4A63D]">Vorbește cu Gabriel</div>
            <div className="text-[10px] text-[#6C7A9C] uppercase tracking-widest mt-0.5">Discuție 1:1</div>
          </div>
        </a>
        <a href="/articole" className="dense-card p-4 flex items-center gap-3 group">
          <BookOpen className="w-5 h-5 text-[#D4A63D]" />
          <div>
            <div className="text-[12.5px] text-[#F8F8F4] font-medium group-hover:text-[#D4A63D]">Articole & ghiduri</div>
            <div className="text-[10px] text-[#6C7A9C] uppercase tracking-widest mt-0.5">Resurse complete</div>
          </div>
        </a>
        <a href={waLink("Salut, am chat-uit cu asistentul AI și aș vrea să continui pe WhatsApp.")} target="_blank" rel="noreferrer" className="dense-card p-4 flex items-center gap-3 group">
          <Phone className="w-5 h-5 text-[#25D366]" />
          <div>
            <div className="text-[12.5px] text-[#F8F8F4] font-medium group-hover:text-[#D4A63D]">Scrie pe WhatsApp</div>
            <div className="text-[10px] text-[#6C7A9C] uppercase tracking-widest mt-0.5">Suport uman</div>
          </div>
        </a>
      </div>
    </div>
  );
}
